import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, Square, X } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { useChatStream } from "@/hooks/useChatStream";
import { formatApiError } from "@/utils/apiError";

const URL_SPLIT_RE = /(https?:\/\/[^\s<>"']+)/gi;
const MD_LINK_SPLIT_RE = /(\[[^\]]+\]\(https?:\/\/[^)\s]+\))/gi;

function splitUrlAndPunctuation(rawUrl) {
  const match = String(rawUrl || "").match(/^(https?:\/\/[^\s<>"']+?)([).,;:!?]+)?$/i);
  if (!match) return { href: rawUrl, trailing: "" };
  return { href: match[1], trailing: match[2] || "" };
}

function linkNode(href, label, key, linkCls) {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={linkCls}
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </a>
  );
}

function linkifyPlainUrls(text, keyPrefix, linkCls) {
  const parts = String(text || "").split(URL_SPLIT_RE);
  return parts.map((part, i) => {
    if (!/^https?:\/\//i.test(part)) return part;
    const { href, trailing } = splitUrlAndPunctuation(part);
    return (
      <span key={`${keyPrefix}-url-${i}`}>
        {linkNode(href, href, `${keyPrefix}-a-${i}`, linkCls)}
        {trailing}
      </span>
    );
  });
}

function linkifyText(text, keyPrefix, linkCls) {
  const parts = String(text || "").split(MD_LINK_SPLIT_RE);
  return parts.map((part, i) => {
    const md = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/i);
    if (md) {
      return linkNode(md[2], md[1], `${keyPrefix}-md-${i}`, linkCls);
    }
    return <span key={`${keyPrefix}-t-${i}`}>{linkifyPlainUrls(part, `${keyPrefix}-${i}`, linkCls)}</span>;
  });
}

function liteMarkdown(text, { isUser, isDark } = {}) {
  const raw = text || "";
  const linkCls = isUser
    ? "underline underline-offset-2 break-all text-white hover:text-white/90"
    : isDark
      ? "underline underline-offset-2 break-all text-blue-400 hover:text-blue-300"
      : "underline underline-offset-2 break-all text-red-600 hover:text-red-700";
  const chunks = raw.split(/(\*\*[^*]+\*\*)/g);
  return chunks.map((chunk, i) => {
    const inner = chunk.startsWith("**") && chunk.endsWith("**") ? chunk.slice(2, -2) : chunk;
    const wrapBold = inner !== chunk;
    const lines = inner.split("\n").map((line, j, arr) => (
      <span key={`${i}-${j}`}>
        {linkifyText(line, `${i}-${j}`, linkCls)}
        {j < arr.length - 1 ? <br /> : null}
      </span>
    ));
    return wrapBold ? <strong key={i}>{lines}</strong> : <span key={i}>{lines}</span>;
  });
}

const CORNER_STYLE = {
  right: "max(1rem, env(safe-area-inset-right))",
  bottom: "max(1rem, env(safe-area-inset-bottom))",
  transformOrigin: "bottom right",
};

const PANEL_SPRING = { type: "spring", stiffness: 340, damping: 24, mass: 0.78 };
const REPLY_SPRING = { type: "spring", stiffness: 380, damping: 22, mass: 0.7 };

function hideOnPath(pathname) {
  return (
    pathname.startsWith("/admin") ||
    pathname.startsWith("/mobile-photos") ||
    pathname.startsWith("/customer-info") ||
    pathname.startsWith("/service-quote")
  );
}

export default function ChatWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [healthy, setHealthy] = useState(null);
  const [leadSent, setLeadSent] = useState(false);
  const [contactPolicyOpen, setContactPolicyOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", email: "", vehicle: "", consent: false });
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  const vehicleId = useMemo(() => {
    const match = location.pathname.match(/^\/inventory\/([^/]+)$/);
    return match ? decodeURIComponent(match[1]) : "";
  }, [location.pathname]);

  const pageUrl = typeof window !== "undefined" ? window.location.href : location.pathname;
  const chat = useChatStream({ language, pageUrl, vehicleId });
  const checkHealth = chat.checkHealth;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const refresh = async () => {
      const ok = await checkHealth();
      if (!cancelled) setHealthy(ok);
    };
    refresh();
    const timer = window.setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, checkHealth]);

  useEffect(() => {
    if (chat.messages.some((msg) => msg.role === "assistant" && (msg.content || "").trim())) {
      setHealthy(true);
    }
  }, [chat.messages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.messages, open]);

  useEffect(() => {
    if (!open) return undefined;
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 220);
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      if (contactPolicyOpen) {
        setContactPolicyOpen(false);
        return;
      }
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, contactPolicyOpen]);

  if (hideOnPath(location.pathname)) return null;

  const openPrivacy = () => {
    navigate("/privacy");
  };
  const accentBg = isDark ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700";
  const linkCls = isDark
    ? "underline underline-offset-2 text-blue-400 hover:text-blue-300"
    : "underline underline-offset-2 text-red-600 hover:text-red-700";
  const panel = isDark ? "bg-[#0A0A0A] border-white/15 text-white" : "bg-white border-gray-200 text-gray-900";
  const muted = isDark ? "text-gray-400" : "text-gray-600";
  const lastAssistant = [...chat.messages].reverse().find((m) => m.role === "assistant")?.content || "";
  const askContact = /phone|teléfono|telefono|name and phone|nombre/i.test(lastAssistant);

  const submit = (event) => {
    event.preventDefault();
    if (!draft.trim() || chat.streaming) return;
    chat.send(draft);
    setDraft("");
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            key="francisco-fab"
            type="button"
            aria-label={t("chat.open")}
            onClick={() => setOpen(true)}
            className={`fixed z-[70] h-14 w-14 rounded-full text-white shadow-lg ${accentBg}`}
            style={CORNER_STYLE}
            data-testid="chat-open-btn"
            initial={{ scale: 0.55, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.35, opacity: 0, x: 8, y: 8 }}
            transition={PANEL_SPRING}
            whileHover={{ scale: 1.07 }}
            whileTap={{ scale: 0.93 }}
          >
            <MessageCircle className="h-6 w-6 mx-auto" />
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {open && (
        <motion.div
          ref={panelRef}
          key="francisco-panel"
          role="dialog"
          aria-label={t("chat.title")}
          className={`fixed z-[70] w-[min(360px,calc(100vw-2rem))] h-[min(480px,calc(100vh-2rem))] border flex flex-col overflow-hidden shadow-2xl ${panel}`}
          style={CORNER_STYLE}
          data-testid="chat-widget"
          initial={{ opacity: 0, scale: 0.16, x: 36, y: 36 }}
          animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, scale: 0.16, x: 36, y: 36 }}
          transition={PANEL_SPRING}
        >
          <motion.div
            className="flex flex-col h-full min-h-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.18 }}
          >
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
            <div>
              <p className="font-unbounded font-semibold">{t("chat.title")}</p>
              <p className={`text-xs ${muted} flex items-center gap-1.5`}>
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${
                    healthy === false ? "bg-red-500" : "bg-emerald-500"
                  }`}
                />
                {healthy === false ? t("chat.statusOffline") : t("chat.statusOnline")}
                <span className={muted}>· {t("chat.subtitle")}</span>
              </p>
            </div>
            <button type="button" aria-label={t("chat.close")} onClick={() => { setContactPolicyOpen(false); setOpen(false); }} className={muted}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm font-['Manrope']">
            {healthy === false && (
              <div className={`p-3 text-xs ${isDark ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-700"}`}>
                {t("chat.healthFail")}
              </div>
            )}
            <div className={`p-3 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>{t("chat.welcome")}</div>
            {chat.messages.map((msg, i) => (
              <motion.div
                key={`${msg.role}-${i}`}
                className={
                  msg.role === "user"
                    ? `ml-6 p-3 text-white ${isDark ? "bg-blue-600" : "bg-red-600"}`
                    : `mr-6 p-3 origin-bottom-left ${isDark ? "bg-white/5" : "bg-gray-50"}`
                }
                initial={
                  msg.role === "assistant"
                    ? { opacity: 0, scale: 0.2, x: -18, y: 22 }
                    : false
                }
                animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                transition={msg.role === "assistant" ? REPLY_SPRING : { duration: 0 }}
              >
                {liteMarkdown(msg.content, { isUser: msg.role === "user", isDark })}
              </motion.div>
            ))}
            {chat.waking && <p className={`text-xs ${muted}`}>{t("chat.waking")}</p>}
            {chat.error && <p className="text-xs text-red-500">{chat.error === "stream_failed" ? t("chat.unavailable") : chat.error}</p>}
            {askContact && !leadSent && (
              <form
                className={`p-3 space-y-2 border ${isDark ? "border-white/10" : "border-gray-200"}`}
                onSubmit={async (event) => {
                  event.preventDefault();
                  try {
                    await chat.submitLead(lead);
                    setLeadSent(true);
                  } catch (error) {
                    chat.setError(formatApiError(error, t("chat.unavailable")));
                  }
                }}
              >
                <input className={`w-full h-9 px-2 text-sm border ${isDark ? "bg-black/40 border-white/10" : "bg-white border-gray-300"}`} placeholder={t("chat.leadName")} value={lead.name} onChange={(e) => setLead((f) => ({ ...f, name: e.target.value }))} />
                <input className={`w-full h-9 px-2 text-sm border ${isDark ? "bg-black/40 border-white/10" : "bg-white border-gray-300"}`} placeholder={t("chat.leadPhone")} value={lead.phone} onChange={(e) => setLead((f) => ({ ...f, phone: e.target.value }))} required />
                <input className={`w-full h-9 px-2 text-sm border ${isDark ? "bg-black/40 border-white/10" : "bg-white border-gray-300"}`} placeholder={t("chat.leadEmail")} value={lead.email} onChange={(e) => setLead((f) => ({ ...f, email: e.target.value }))} />
                <div className="flex items-start gap-2">
                  <input
                    id="chat-contact-consent"
                    type="checkbox"
                    checked={lead.consent}
                    onChange={(event) => setLead((current) => ({ ...current, consent: event.target.checked }))}
                    className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isDark ? "accent-blue-500" : "accent-red-600"}`}
                    data-testid="chat-contact-consent"
                  />
                  <p className={`text-xs leading-snug ${muted}`}>
                    <label htmlFor="chat-contact-consent" className="cursor-pointer">{t("chat.consentBefore")}</label>
                    {" "}
                    <button
                      type="button"
                      className={linkCls}
                      data-testid="chat-contact-policy"
                      onClick={() => setContactPolicyOpen(true)}
                    >
                      {t("chat.contactPolicy")}
                    </button>
                    {" "}
                    <label htmlFor="chat-contact-consent" className="cursor-pointer">{t("chat.consentAnd")}</label>
                    {" "}
                    <button
                      type="button"
                      className={linkCls}
                      data-testid="chat-privacy-policy"
                      onClick={openPrivacy}
                    >
                      {t("chat.privacyPolicy")}
                    </button>
                  </p>
                </div>
                <button type="submit" className={`w-full py-2 text-white text-xs uppercase tracking-wider ${accentBg}`}>{t("chat.leadSubmit")}</button>
              </form>
            )}
            {leadSent && <p className={`text-xs ${muted}`}>{t("chat.leadThanks")}</p>}
            <div ref={endRef} />
          </div>

          <form onSubmit={submit} className={`p-3 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
            <p className={`text-[10px] mb-2 ${muted}`}>{t("chat.disclaimer")}</p>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={chat.streaming}
                maxLength={500}
                placeholder={t("chat.placeholder")}
                className={`flex-1 h-10 px-3 text-sm border ${isDark ? "bg-black/40 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"}`}
              />
              {chat.streaming ? (
                <button type="button" aria-label={t("chat.stop")} onClick={chat.stop} className={`h-10 w-10 text-white ${accentBg}`}>
                  <Square className="h-4 w-4 mx-auto" />
                </button>
              ) : (
                <button type="submit" aria-label={t("chat.send")} disabled={!draft.trim()} className={`h-10 w-10 text-white disabled:opacity-40 ${accentBg}`}>
                  <Send className="h-4 w-4 mx-auto" />
                </button>
              )}
            </div>
          </form>
          </motion.div>
        </motion.div>
        )}
      </AnimatePresence>
      {open && contactPolicyOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60"
          role="presentation"
          onClick={() => setContactPolicyOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="chat-contact-policy-title"
            data-testid="chat-contact-policy-dialog"
            className={`w-full max-w-sm border p-4 shadow-2xl ${panel}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <p id="chat-contact-policy-title" className="font-unbounded text-sm font-semibold">
                {t("chat.contactPolicyTitle")}
              </p>
              <button
                type="button"
                aria-label={t("chat.close")}
                onClick={() => setContactPolicyOpen(false)}
                className={muted}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm font-['Manrope'] leading-relaxed">{t("chat.contactPolicyBody")}</p>
          </div>
        </div>
      )}
    </>
  );
}
